import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';

import {
  ConfigureEventStore,
  convertToFullTemplate
} from 'src/app/features/events/state/configure-events.state';
import { EmailTemplateStore } from 'src/app/shared/services/email-template/state/email-template.state';
import { mergeNotificationEmails } from 'src/app/shared/services/email/notification-email.functions';
import { SelectedEventDetailsStore } from 'src/app/shared/services/events/events.state';
import { AxleToastService } from 'src/app/shared/services/toast/axle-toast.service';
import { UserStore } from 'src/app/shared/services/user/user.state';

export const createEventResolver: ResolveFn<boolean> = () => {
  const selectedEventStore = inject(SelectedEventDetailsStore);
  const configureEventStore = inject(ConfigureEventStore);
  const emailTemplateStore = inject(EmailTemplateStore);
  const userStore = inject(UserStore);
  const toast = inject(AxleToastService);
  const router = inject(Router);

  configureEventStore.resetState({
    configureMode: 'CREATE',
    feature: 'FULL_EVENT'
  });

  const navigation = router.currentNavigation();
  const eventId = navigation?.extras?.state?.['cloneEventId'];

  const defaultFromEmail = userStore.currentDefaultFromEmail();
  if (!defaultFromEmail) return false;

  const cachedTemplates = emailTemplateStore.defaultTemplates();

  if (cachedTemplates) {
    const notificationEmails = convertToFullTemplate(
      cachedTemplates,
      defaultFromEmail
    );
    configureEventStore.updateState({ notificationEmails });
  }

  if (eventId) {
    return new Promise<boolean>((resolve) => {
      const fetchDefaultsPromise = new Promise<void>((resolveDefaults) => {
        if (cachedTemplates) {
          resolveDefaults();
        } else {
          emailTemplateStore.getDefaultNotificationEmails({
            onSuccess: (defaultEmails) => {
              const notificationEmails = convertToFullTemplate(
                defaultEmails,
                defaultFromEmail
              );
              configureEventStore.updateState({ notificationEmails });
              resolveDefaults();
            },
            onFail: () => {
              toast.addError('Failed to get default notification emails');
              resolveDefaults();
            }
          });
        }
      });

      fetchDefaultsPromise.then(() => {
        selectedEventStore.cloneEvent({
          eventId,
          onSuccess: (details) => {
            const currentDefaultEmails =
              configureEventStore.notificationEmails();

            const mergedNotificationEmails = mergeNotificationEmails(
              currentDefaultEmails,
              details.notificationEmails
            );

            configureEventStore.setEventDetails(details, 'CREATE');

            configureEventStore.updateState({
              notificationEmails: mergedNotificationEmails
            });

            resolve(true);
          },
          onFail: () => {
            toast.addError('Failed to clone event');
            resolve(false);
          }
        });
      });
    });
  } else {
    configureEventStore.getDefaultNotificationEmails();
    return true;
  }
};

export const clearEventUpdates: ResolveFn<boolean> = () => {
  const configureEventStore = inject(ConfigureEventStore);

  configureEventStore.clearUpdates();
  return true;
};
